import type {
  GroupedMemberProfileMapCoordsList,
  MemberProfileMapCoordsList,
  MemberSignInEntryCoordsResponse
} from '@library/types/memberProfileMap';
import { chain, collectBy, identity, prop } from 'ramda';
import { MEMBER_COMMON_COORDS_HEADERS } from '@bo-constants/member/memberProfile';
import { dateDisplay } from '@bo-utils/members/memberCommon';

export const transformMemberSignInCoords = ({
  memberSignInCoordsResponse
}: {
  memberSignInCoordsResponse: MemberSignInEntryCoordsResponse | undefined;
}): GroupedMemberProfileMapCoordsList | undefined => {
  const { data, included } = memberSignInCoordsResponse || {};

  if (!data) {
    return undefined;
  }

  const formattedCoords: MemberProfileMapCoordsList = data.map((coords) => {
    const { relationships } = coords;

    const {
      attributes: {
        city,
        country_code: country,
        created_at: createdAt,
        ip_address: ipAddress,
        latitude,
        longitude,
        state
      },
      id,
      type
    } = coords;

    const creationDateETString = dateDisplay(createdAt, true);

    const roundedLatitude = latitude ? latitude.toFixed(3) : latitude;
    const roundedlongitude = longitude ? longitude.toFixed(3) : longitude;

    const allowedStateName = included?.find(
      (includedItem) => includedItem.id === relationships?.allowed_state.data.id
    )?.attributes.name;
    const stateName = state ?? allowedStateName;

    return {
      city: city ?? '-',
      country: country ?? '-',
      coordsString: `${roundedLatitude}-${roundedlongitude}`,
      createdAt,
      createdAtDisplay: creationDateETString,
      id,
      ipAddress: ipAddress ?? '-',
      latitude,
      longitude,
      position: [Number(roundedLatitude), Number(roundedlongitude)],
      state: stateName ?? '-',
      type
    };
  });

  // Group the rows by latitude and longitude
  const groupedCoords = collectBy(prop('coordsString'), formattedCoords);

  // Sort the Grouped rows by createdAt
  const sortedGroupedCoords = groupedCoords.map((coordsList) =>
    coordsList.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
  );

  return sortedGroupedCoords;
};

export const getMemberLocationTableHeaders = (entriesChecked: boolean) => {
  const colWidth = 'w-[12.5%]';

  return [
    {
      column: 'id',
      essential: true,
      label: entriesChecked ? 'Entry ID' : 'ID',
      sortEnabled: false,
      width: colWidth
    },
    ...MEMBER_COMMON_COORDS_HEADERS,
    {
      column: 'created_at',
      essential: true,
      label: entriesChecked ? 'Created at' : 'Sign in at',
      sortEnabled: false,
      width: colWidth
    }
  ];
};

export const getCombinedMapCoordsList = (
  mapData?: GroupedMemberProfileMapCoordsList
) => chain(identity, mapData ?? []);
